import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isDate } from 'date-fns';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import logger from '@widgets/logger';
import { useTranslation, useYupValidationResolver, useChannels } from '@hooks';
import { CHANNEL } from '@constants/channels';
import { alertActions, campaignsActions } from '@actions';
import { campaignsService } from '@services';
import { ModalType } from '@constants/modal';
import { CAMPAIGN_TYPE, MAX_LENGTH } from '@constants/campaigns';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { handleApiError } from '@helpers/api';
import { testEditorLength } from '@helpers/text-editor';
import ContentSource, { MESSAGE_TYPE } from './ContentSource';
import PrimaryFields from './PrimaryFields';
import SecondaryFields from './SecondaryFields';
import LabelsFields from './LabelsField';
import Frequency, { FrequencyMap } from './Frequency';
import { Modal, Content } from './styles';

const EditModal = ({
    modal,
    setModal,
    campaignType,
    brands,
    selectedCampaign,
    setSelectedCampaign,
    rowsPerPage,
    onDelete,
}) => {
    const { t } = useTranslation();

    const [contactsNumber, setContactsNumber] = useState(0);
    const [channelType, setChannelType] = useState(CHANNEL.SMS);
    const [messageType, setMessageType] = useState(MESSAGE_TYPE.MESSAGE);

    const contactsNumberRef = useRef(0);

    const { data: labels } = useSelector(state => state.labels);
    const { data } = useSelector(state => state.campaigns);
    const channels = useSelector(state => state.channels.data);
    const dispatch = useDispatch();
    const { channelIsAssignedToBrandOrLocation } = useChannels();

    const IS_EMAIL =
        channelType === CHANNEL.EMAIL && (messageType === MESSAGE_TYPE.MESSAGE || messageType === MESSAGE_TYPE.COUPON);
    const IS_WHATSAPP = channelType === CHANNEL.WHATSAPP;
    const IS_WHATSAPP_BUSINESS = channelType === CHANNEL.WHATSAPP_BUSINESS;

    const CampaignSchema = yup.object().shape({
        campaignType: yup.number(),
        messageType: yup.number(),
        title: yup.string().required('validation.required'),
        locationId: yup.string().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) =>
                locationType === LOCATION_TYPE.LOCATION || (locationType === LOCATION_TYPE.BRAND && !brandAccess),
            then: yup.string().required('validation.required'),
            otherwise: yup.string().nullable(),
        }),
        brandId: yup.string().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) => locationType === LOCATION_TYPE.BRAND && brandAccess,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().nullable(),
        }),
        channelId: yup.string().required('validation.required'),
        scheduledTime: yup.string().when('campaignType', {
            is: () => campaignType === CAMPAIGN_TYPE.ONGOING,
            then: yup.string().notRequired(),
            otherwise: yup.string().nullable().required('validation.required'),
        }),
        triggers: yup.array().when('campaignType', {
            is: () => campaignType === CAMPAIGN_TYPE.ONGOING,
            then: yup.array().min(1, 'validation.required'),
            otherwise: yup.array().notRequired(),
        }),
        labels: yup.array().when('campaignType', {
            is: () => campaignType === CAMPAIGN_TYPE.ONGOING,
            then: yup.array().notRequired(),
            otherwise: yup.array().test('messages-quota', 'messages-quota', () => contactsNumberRef.current > 0),
        }),
        message: yup.string().when('messageType', {
            is: messageType =>
                (messageType === MESSAGE_TYPE.MESSAGE || messageType === MESSAGE_TYPE.COUPON) && !IS_WHATSAPP_BUSINESS,
            then: yup
                .string()
                .required('validation.required')
                .test('text-editor-length', `validation.maxLength`, value => {
                    return testEditorLength(value, MAX_LENGTH[channelType]);
                }),
            otherwise: yup.string().nullable().notRequired(),
        }),
        subject: yup.string().when('messageType', {
            is: () => IS_EMAIL,
            then: yup.string().required('validation.required').max(MAX_LENGTH[channelType], 'validation.maxLength'),
            otherwise: yup.string().nullable().notRequired(),
        }),
        coverPhotoEnabled: yup.boolean(),
        coverPhotoUrl: yup.string().when('coverPhotoEnabled', {
            is: val => !!val && (IS_EMAIL || IS_WHATSAPP),
            then: yup.string().required('validation.required'),
            otherwise: yup.string().nullable(),
        }),
        ctaButtonEnabled: yup.boolean(),
        ctaTitle: yup.string().when('ctaButtonEnabled', {
            is: val => !!val && IS_EMAIL,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().nullable(),
        }),
        ctaLink: yup.string().when('ctaButtonEnabled', {
            is: val => !!val && IS_EMAIL,
            then: yup.string().url('validation.invalidURL').required('validation.required'),
            otherwise: yup.string().nullable(),
        }),
        ctaColor: yup.string().when('ctaButtonEnabled', {
            is: val => !!val && IS_EMAIL,
            then: yup.string().required('validation.required').validateColor('validation.hexFormat'),
            otherwise: yup.string().nullable(),
        }),
        surveyTemplateId: yup.string().when('messageType', {
            is: messageType => messageType === MESSAGE_TYPE.SURVEY,
            then: yup.string().nullable().required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        whatsAppTemplateId: yup.string().when('channelId', {
            is: val => val && channelType === CHANNEL.WHATSAPP_BUSINESS,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        couponTemplateId: yup.string().when('messageType', {
            is: messageType => messageType === MESSAGE_TYPE.COUPON,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().nullable().notRequired(),
        }),
    });

    const resolver = useYupValidationResolver(CampaignSchema);

    const {
        getValues,
        setValue,
        control,
        reset,
        watch,
        trigger,
        handleSubmit,
        formState: { isSubmitting, isValid, isDirty },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            title: '',
            message: '',
            subject: '',
            channelId: '',
            locationId: '',
            brandId: '',
            locationType: LOCATION_TYPE.BRAND,
            brandAccess: false,
            labels: [],
            scheduledTime: '',
            triggers: [1, 5, 7, 8, 9, 10],
            delay: 0,
            surveyTemplateId: '',
            coverPhotoEnabled: false,
            coverPhotoUrl: '',
            ctaButtonEnabled: false,
            ctaTitle: '',
            ctaLink: '',
            ctaColor: '',
            messageType: MESSAGE_TYPE.MESSAGE,
            onePerContact: FrequencyMap.EveryTime,
            whatsAppTemplateId: '',
            couponTemplateId: '',
        },
    });

    useEffect(() => {
        const isNewCampaign = modal.type === ModalType.Create;
        if (!isNewCampaign) {
            setValue('title', selectedCampaign?.title || '', { shouldValidate: true });
            setValue('message', selectedCampaign?.message || '', { shouldValidate: true });
            setValue('subject', selectedCampaign?.subject || '', { shouldValidate: true });
            setValue('coverPhotoEnabled', !!selectedCampaign?.coverPhotoEnabled, { shouldValidate: true });
            setValue('coverPhotoUrl', selectedCampaign?.coverPhotoUrl || '', { shouldValidate: true });
            setValue('ctaButtonEnabled', !!selectedCampaign?.ctaButtonEnabled, { shouldValidate: true });
            setValue('ctaTitle', selectedCampaign?.ctaTitle || '', { shouldValidate: true });
            setValue('ctaLink', selectedCampaign?.ctaLink || '', { shouldValidate: true });
            setValue('ctaColor', selectedCampaign?.ctaColor || '', { shouldValidate: true });
            const isChannelAssignedToBrandOrLocation = channelIsAssignedToBrandOrLocation(
                selectedCampaign?.channelId,
                selectedCampaign?.brandId,
                selectedCampaign?.locationId,
            );
            setValue(
                'channelId',
                selectedCampaign?.channelId === CHANNEL.ARCHIVED
                    ? 'ARCHIVED'
                    : isChannelAssignedToBrandOrLocation
                    ? selectedCampaign?.channelId
                    : '',
                { shouldValidate: true },
            );
            setValue('whatsAppTemplateId', selectedCampaign?.whatsAppTemplateId || '', { shouldValidate: true });
            setValue('labels', selectedCampaign?.labels.map(l => l.id) || []);
            if (campaignType === CAMPAIGN_TYPE.ONETIME) {
                setValue('scheduledTime', new Date(selectedCampaign?.scheduledTime), {
                    shouldValidate: true,
                });
            } else {
                setValue('delay', selectedCampaign?.delay);
                setValue('triggers', selectedCampaign?.triggers || []);
                setValue('onePerContact', selectedCampaign?.onePerContact ? FrequencyMap.Once : FrequencyMap.EveryTime);
            }
            if (selectedCampaign?.surveyTemplateId?.length > 0) {
                setValue('surveyTemplateId', selectedCampaign?.surveyTemplateId, { shouldValidate: true });
                setValue('messageType', MESSAGE_TYPE.SURVEY, { shouldValidate: true });
                setMessageType(MESSAGE_TYPE.SURVEY);
            }
            if (selectedCampaign?.couponTemplateId) {
                setValue('couponTemplateId', selectedCampaign?.couponTemplateId, { shouldValidate: true });
                setValue('messageType', MESSAGE_TYPE.COUPON, { shouldValidate: true });
                setMessageType(MESSAGE_TYPE.COUPON);
            }
            const channel = channels.find(c => c.id === selectedCampaign?.channelId);
            setChannelType(channel?.type || CHANNEL.SMS);
        }
    }, [selectedCampaign, channels, setValue, modal.type, campaignType]);

    const onCloseModal = () => {
        setModal({ open: false, type: ModalType.Create });
        setSelectedCampaign(null);
        reset();
    };

    const getContactsNumber = async () => {
        const values = getValues();
        try {
            const response = await campaignsService.getSelectedContactsNumber({
                ...selectedCampaign,
                ...values,
                scheduledTime: isDate(values.scheduledTime)
                    ? new Date(values.scheduledTime).toISOString()
                    : new Date().toISOString(),
                labels: values.labels.length > 0 ? labels.filter(label => values.labels.includes(label.id)) : [],
                ...(modal.type === ModalType.Edit && { id: selectedCampaign?.id }),
            });
            setContactsNumber(response?.count);
            contactsNumberRef.current = response?.count;
            trigger('labels');
        } catch (_) {
            logger.error("Couldn't get contacts number");
        }
    };

    const onSaveCampaign = async () => {
        const values = getValues();

        try {
            const newCampaign = await campaignsService.createOrUpdateCampaign(
                {
                    ...selectedCampaign,
                    ...values,
                    message:
                        messageType === MESSAGE_TYPE.MESSAGE || messageType === MESSAGE_TYPE.COUPON
                            ? values.message
                            : '',
                    surveyTemplateId: messageType === MESSAGE_TYPE.SURVEY ? values.surveyTemplateId : null,
                    couponTemplateId: messageType === MESSAGE_TYPE.COUPON ? values.couponTemplateId : null,
                    ...(campaignType === CAMPAIGN_TYPE.ONETIME && {
                        scheduledTime: new Date(values.scheduledTime).toISOString(),
                    }),
                    labels: labels.filter(label => values.labels.includes(label.id)),
                    onePerContact: +values.onePerContact === FrequencyMap.Once,
                    ...(modal.type === ModalType.Edit && { id: selectedCampaign?.id }),
                },
                campaignType,
            );
            if (modal.type === ModalType.Create) {
                dispatch(campaignsActions.get(0, rowsPerPage, campaignType));
            } else {
                const campaignsCopy = data.slice();
                const updatedCampaignIndex = campaignsCopy.findIndex(c => c.id === selectedCampaign?.id);
                if (updatedCampaignIndex !== -1 && newCampaign) {
                    campaignsCopy[updatedCampaignIndex] = newCampaign;
                    dispatch(campaignsActions.setCampaigns(campaignsCopy));
                }
                setSelectedCampaign(newCampaign);
            }
            dispatch(
                alertActions.success(
                    t(`alertMessages.${modal.type === ModalType.Create ? 'createSuccess' : 'editSuccess'}`),
                ),
            );
            onCloseModal();
        } catch (error) {
            handleApiError(error, `alertMessages.${modal.type === ModalType.Create ? 'createFail' : 'editFail'}`);
        }
    };

    const isView = modal.type === ModalType.View;

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onCloseModal}
            title={
                modal.type === ModalType.Create
                    ? t('CampaignsPage.createTitle')
                    : isView
                    ? t('CampaignsPage.viewTitle')
                    : t('CampaignsPage.editTitle')
            }
            onPrimaryAction={isView ? null : handleSubmit(onSaveCampaign)}
            primaryActionText={
                modal.type === ModalType.Create ? t('buttons.cheduleCampaign') : t('buttons.saveChanges')
            }
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onSecondaryAction={onCloseModal}
            secondaryActionText={t('buttons.cancel')}
            onAdditionalAction={modal.type !== ModalType.Edit ? null : () => onDelete(selectedCampaign)}
            additionalTextButton={modal.type !== ModalType.Edit ? null : t('buttons.delete')}
        >
            <Content>
                <FormProvider {...{ setValue, watch, control, trigger, selectedCampaign }}>
                    <PrimaryFields
                        campaignType={campaignType}
                        isView={isView}
                        isEdit={modal.type === ModalType.Edit}
                        brands={brands}
                        channels={channels}
                        setChannelType={setChannelType}
                        getContactsNumber={getContactsNumber}
                    />
                    <SecondaryFields campaignType={campaignType} isView={isView} />
                    <LabelsFields
                        isView={isView}
                        campaignType={campaignType}
                        channelType={channelType}
                        getContactsNumber={getContactsNumber}
                        contactsNumber={contactsNumber}
                        labels={labels}
                    />
                    {campaignType === CAMPAIGN_TYPE.ONGOING && <Frequency isView={isView} />}
                    <ContentSource
                        campaignType={campaignType}
                        channelType={channelType}
                        brands={brands}
                        isView={isView}
                        messageType={messageType}
                        setMessageType={setMessageType}
                    />
                </FormProvider>
            </Content>
        </Modal>
    );
};

export default EditModal;
